import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import AdminTopNav from './components/AdminTopNav'
import ErrorBoundary from './components/ErrorBoundary'
import NoAccessScreen from './components/NoAccessScreen'
// Páginas cargadas bajo demanda (code-splitting): el wizard público no arrastra
// la app admin y viceversa → primera carga mucho más liviana, sobre todo móvil.
const Dashboard = lazy(() => import('./pages/Dashboard'))
const ListadoOportunidades = lazy(() => import('./pages/ListadoOportunidades'))
const NuevaOportunidad = lazy(() => import('./pages/NuevaOportunidad'))
const BulkOportunidades = lazy(() => import('./pages/BulkOportunidades'))
const DetalleOportunidad = lazy(() => import('./pages/DetalleOportunidad'))
const Proveedores = lazy(() => import('./pages/Proveedores'))
const ProveedorDetalle = lazy(() => import('./pages/ProveedorDetalle'))
const AdminConfig = lazy(() => import('./pages/AdminConfig'))
const BandejaRevision = lazy(() => import('./pages/BandejaRevision'))
const ComplementosPendientes = lazy(() => import('./pages/ComplementosPendientes'))
const PagosCuentasPorPagar = lazy(() => import('./pages/PagosCuentasPorPagar'))
const PortalLogin = lazy(() => import('./pages/PortalLogin'))
const PortalVerifyOtp = lazy(() => import('./pages/PortalVerifyOtp'))
const PortalDashboard = lazy(() => import('./pages/PortalDashboard'))
const PortalExpediente = lazy(() => import('./pages/PortalExpediente'))
const PortalEquipo = lazy(() => import('./pages/PortalEquipo'))
const RegistroDistribuidor = lazy(() => import('./pages/RegistroDistribuidor'))
const Correccion = lazy(() => import('./pages/Correccion'))
import { AuthProvider } from './auth/authProvider'
import { useRole } from './auth/useRole'
import { ToastProvider } from './components/ui/Toast'

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  )
}

/**
 * Guardia de ruta: renderiza el contenido solo si `ok`; si no, la pantalla
 * "Sin acceso a esta funcionalidad". Es la defensa por deep-link (URL directa);
 * el menú ya oculta lo que el rol no puede usar. La frontera de seguridad REAL
 * es el backend (`permissions.guard`); esto es solo UX.
 */
function Guard({ ok, children }: { ok: boolean; children: ReactNode }) {
  return ok ? <>{children}</> : <NoAccessScreen variant="feature" />
}

function AuthenticatedContent() {
  const { loading, roles, can } = useRole()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (roles.length === 0) {
    return <NoAccessScreen />
  }

  return (
    <Routes>
      {/* Universales (todos los roles con rol asignado) */}
      <Route path="/" element={<Dashboard />} />
      <Route path="/oportunidades" element={<ListadoOportunidades />} />
      <Route path="/oportunidades/:id" element={<DetalleOportunidad />} />
      {/* Gateadas por capability */}
      <Route path="/oportunidades/nueva" element={<Guard ok={can.create_oportunidad}><NuevaOportunidad /></Guard>} />
      <Route path="/oportunidades/bulk" element={<Guard ok={can.create_oportunidad}><BulkOportunidades /></Guard>} />
      <Route path="/revision" element={<Guard ok={can.review_cfdi_read}><BandejaRevision /></Guard>} />
      <Route path="/complementos-pendientes" element={<Guard ok={can.view_complementos}><ComplementosPendientes /></Guard>} />
      <Route path="/pagos" element={<Guard ok={can.view_pagos}><PagosCuentasPorPagar /></Guard>} />
      <Route path="/proveedores" element={<Guard ok={can.view_proveedores}><Proveedores /></Guard>} />
      <Route path="/proveedores/:id" element={<Guard ok={can.view_proveedores}><ProveedorDetalle /></Guard>} />
      {/* Legacy redirects — los enlaces existentes siguen funcionando. */}
      <Route path="/admin/proveedores" element={<Navigate to="/proveedores?tab=catalogo" replace />} />
      <Route path="/admin/registros-distribuidor" element={<Navigate to="/proveedores?tab=solicitudes" replace />} />
      <Route path="/admin/config" element={<Guard ok={can.admin_config || can.manage_entidades}><AdminConfig /></Guard>} />
    </Routes>
  )
}

function AdminShell() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-slate-50 dark:bg-surface-950">
        <AdminTopNav />
        <main>
          <Suspense fallback={<PageFallback />}>
            <AuthenticatedContent />
          </Suspense>
        </main>
      </div>
    </AuthProvider>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <BrowserRouter>
          {/* Los flags v7_startTransition / v7_relativeSplatPath ya son el
              comportamiento por defecto en react-router 7 (prop `future` retirado). */}
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Portal del proveedor (Chunk HH) — auth con email + RFC + OTP. */}
            <Route path="/p/login" element={<PortalLogin />} />
            <Route path="/p/login/verify" element={<PortalVerifyOtp />} />
            <Route path="/p" element={<PortalDashboard />} />
            <Route path="/p/expediente/:id" element={<PortalExpediente />} />
            <Route path="/p/equipo" element={<PortalEquipo />} />

            {/* Alta pública de distribuidor — sin token, sin login (Chunk GG). */}
            <Route path="/registro-distribuidor" element={<RegistroDistribuidor />} />

            {/* Corrección/reenvío de documentos por token (Fase B). */}
            <Route path="/correccion" element={<Correccion />} />

            {/* Resto de la app — requiere login Entra ID */}
            <Route path="/*" element={<AdminShell />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  )
}
